import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';
import moment from 'moment';

import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { UsersDatacenter } from '../../../../users/models/user';
import { UserService } from '../../../../users/services/user.service';
import { Strategy } from '../strategy';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY, FilterDateFormatType,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';

export class StrategyUsers implements Strategy<UsersDatacenter> {
	userService: UserService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof UsersDatacenter>('id', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.userService = injector.get<UserService>(UserService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof UsersDatacenter>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((users: PaginatedResult<UsersDatacenter>) => this.removeDeselectedRowsFromGrid(users));
	}

	async getAllData(
		orderBy: OrderBy<keyof UsersDatacenter>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.userService.getUsersDatacenter(
			<(keyof UsersDatacenter)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<UsersDatacenter>, data: UsersDatacenter) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined>data[<keyof UsersDatacenter>field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return fields.length > 0 ? <string>data[<keyof UsersDatacenter>fields[0]]
				: <string>data[<keyof UsersDatacenter>field];
		case ColumnType.TRANSLATED_COLUMN:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			return this.getNestedProperty(data, columnDef.filterParams.userFields[1]
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				? <string>columnDef.filterParams.userFields[1]
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				: <string>columnDef.filterParams.userFields[0]);
		case ColumnType.DATE_COLUMN:
			if (data[<keyof UsersDatacenter>field]) {
				const dateformat = (<FilterDateFormatType>columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment.utc(<string>data[<keyof UsersDatacenter>field]).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any,
	getNestedProperty(obj: any, path: string) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
		return <string>path.split('.').reduce((acc, part) => acc?.[part], obj);
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<UsersDatacenter>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: UsersDatacenter }) =>
				data.memberId ? String(data.id) + data.memberId : String(data.id)
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof UsersDatacenter>[],
		currentColumns: (keyof UsersDatacenter)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<UsersDatacenter>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.userService.getUsersDatacenter(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).subscribe({
			next: res => {
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting user datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: (keyof UsersDatacenter)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		if (!currentColumns.includes('creationDate')) {
			currentColumns.push('creationDate');
		}
		if (!currentColumns.includes('memberId')) {
			currentColumns.push('memberId');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRowsFromGrid(users: PaginatedResult<UsersDatacenter>) {
		return users.results.filter(user => {
			const rowDeselected = this.selectionService.findDeselectedNode(String(user.id));
			return !rowDeselected;
		});
	}
}
